import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { SwapHoriz } from "@mui/icons-material";

import {
  maskDate,
  maskIdentification,
  maskPhone,
  maskCep,
  maskCnpj,
} from "../../../utils/masks";
import { validateIdentification } from "../../../utils/validation";
import AddressList from "./Address/AddressList";
import AddressFormModal from "./Address/AddressFormModal";
import {
  UserAddress,
  UserAddressPayload,
  UserAddressPatchPayload,
  UserInfoFormController,
  UserInfoFormControllerDelegate,
} from "./Address/controller";
import {
  UpdateUserProfilePayload,
  UserProfile,
} from "../../../clients/answeringClient";

const sanitizeDigits = (value: string | undefined | null) =>
  (value ?? "").replace(/\D/g, "");

const deriveIdentificationType = (
  digits: string
): "CPF" | "CNPJ" | undefined => {
  if (digits.length === 11) {
    return "CPF";
  }
  if (digits.length === 14) {
    return "CNPJ";
  }
  return undefined;
};

const isValidBirthDate = (birthDate: {
  day: string;
  month: string;
  year: string;
}) => {
  const day = Number.parseInt(birthDate.day, 10);
  const month = Number.parseInt(birthDate.month, 10);
  const year = Number.parseInt(birthDate.year, 10);

  if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) {
    return false;
  }

  if (day < 1 || day > 31) return false;
  if (month < 1 || month > 12) return false;
  if (year <= 1900) return false;

  return true;
};

const mapBirthDateFromIso = (value?: string) => {
  if (!value) {
    return { day: "", month: "", year: "" };
  }
  const [datePart] = value.split("T");
  const [year, month, day] = (datePart ?? "").split("-");
  const pad = (input: string | undefined, size: number) =>
    input && input.length > 0 ? input.padStart(size, "0") : "";
  return {
    day: pad(day, 2),
    month: pad(month, 2),
    year: pad(year, 4),
  };
};

const convertProfileToUserData = (profile: UserProfile) => {
  const birthDateParts = mapBirthDateFromIso(profile.birthDate);
  const isCnpj = profile.identificationType === "CNPJ";
  return {
    identification: isCnpj ? "" : sanitizeDigits(profile.identification),
    identificationType: isCnpj ? undefined : profile.identificationType,
    cnpj: isCnpj ? sanitizeDigits(profile.identification) : undefined,
    birthDate: birthDateParts,
    cellphone: sanitizeDigits(profile.contact?.phoneNumber),
  };
};

const formatBirthDateForApi = (birthDate: {
  day: string;
  month: string;
  year: string;
}) => {
  if (!birthDate.day || !birthDate.month || !birthDate.year) {
    return undefined;
  }

  if (
    birthDate.day.length !== 2 ||
    birthDate.month.length !== 2 ||
    birthDate.year.length !== 4
  ) {
    return undefined;
  }

  return `${birthDate.year}-${birthDate.month}-${birthDate.day}`;
};

const buildUserProfilePayload = (
  user: UserData
): UpdateUserProfilePayload | undefined => {
  const identification = sanitizeDigits(user.identification);
  const identificationType =
    user.identificationType ?? deriveIdentificationType(identification);
  const birthDate = formatBirthDateForApi(user.birthDate);
  const phoneNumber = sanitizeDigits(user.cellphone);

  if (
    !identification ||
    !identificationType ||
    !birthDate ||
    phoneNumber.length < 10
  ) {
    return undefined;
  }

  return {
    identification,
    identificationType,
    birthDate,
    contact: {
      type: "MOBILE",
      phoneNumber,
    },
  };
};

const getUserAddressLabel = (address: UserAddress) => {
  const label = address.label?.trim();
  if (label && label.length > 0) {
    return label;
  }
  return `${address.street}, ${address.number}`;
};

const buildPrimaryHeading = (address: UserAddress | undefined) =>
  address
    ? address.label?.trim() || `${address.street}, ${address.number}`
    : "";

const buildSecondaryHeading = (address: UserAddress | undefined) => {
  if (!address) return "";
  const addressLine = `${address.street}, ${address.number}`.trim();
  const customLabel = address.label?.trim();
  if (customLabel && customLabel !== addressLine) {
    return addressLine;
  }
  if (address.completion) {
    return address.completion.trim();
  }
  return "";
};

type InputChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

export type UserData = {
  identification: string;
  identificationType?: "CPF" | "CNPJ";
  cnpj?: string;
  birthDate: {
    day: string;
    month: string;
    year: string;
  };
  cellphone: string;
};

export type UserErrors = {
  identification: boolean;
  birthDate: boolean;
  cellphone: boolean;
  cnpj: boolean;
};

export type UserProfileState = {
  loading: boolean;
  saving: boolean;
  prefilled: boolean;
  dirty: boolean;
  shouldSave: boolean;
  error?: string;
};

type AddressConfirmAction =
  | { type: "delete"; address: UserAddress }
  | { type: "default"; address: UserAddress };

type AddressUIState = {
  items: UserAddress[];
  loading: boolean;
  error?: string;
  selectedId?: number;
  modalOpen: boolean;
  editing?: UserAddress | null;
  confirmAction?: AddressConfirmAction | null;
};

export type AddressStateSnapshot = {
  loading: boolean;
  selectedAddressId?: number;
  hasAddresses: boolean;
  error?: string;
};

const INITIAL_ERRORS: UserErrors = {
  identification: false,
  birthDate: false,
  cellphone: false,
  cnpj: false,
};

const INITIAL_PROFILE_STATE: UserProfileState = {
  loading: false,
  saving: false,
  prefilled: false,
  dirty: false,
  shouldSave: false,
  error: undefined,
};

const INITIAL_ADDRESS_STATE: AddressUIState = {
  items: [],
  loading: true,
  error: undefined,
  selectedId: undefined,
  modalOpen: false,
  editing: null,
  confirmAction: null,
};

export interface UserInfoFormHandle {
  ensureProfilePersisted: () => Promise<boolean>;
  isBusy: () => boolean;
}

interface UserInfoFormProps {
  user: UserData;
  onUserChange: (user: UserData, errors: UserErrors) => void;
  loadUserProfile?: () => Promise<UserProfile | undefined>;
  saveUserProfile?: (
    payload: UpdateUserProfilePayload
  ) => Promise<UserProfile | undefined>;
  onProfileStateChange?: (state: UserProfileState) => void;
  getAccessToken?: () => string | null;
  onLogout?: () => void;
  onAddressSelected?: (address?: UserAddress) => void;
  onAddressStateChange?: (state: AddressStateSnapshot) => void;
}

const UserInfoFormComponent = (
  {
    user,
    onUserChange,
    loadUserProfile,
    saveUserProfile,
    onProfileStateChange,
    getAccessToken,
    onLogout,
    onAddressSelected,
    onAddressStateChange,
  }: UserInfoFormProps,
  ref: React.Ref<UserInfoFormHandle>
) => {
  const [errors, setErrors] = useState<UserErrors>(INITIAL_ERRORS);
  const [profileState, setProfileState] = useState<UserProfileState>(
    INITIAL_PROFILE_STATE
  );
  const [addressState, setAddressState] = useState<AddressUIState>(
    INITIAL_ADDRESS_STATE
  );
  const addressesForcedSelection = useRef(false);
  const hasLoadedAddressesRef = useRef(false);
  const onLogoutRef = useRef(onLogout);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  useEffect(() => {
    onProfileStateChange?.(profileState);
  }, [profileState, onProfileStateChange]);

  const emitUserChange = useCallback(
    (
      nextUser: UserData,
      nextErrors: UserErrors,
      options?: { markDirty?: boolean }
    ) => {
      setErrors(nextErrors);
      onUserChange(nextUser, nextErrors);

      if (options?.markDirty) {
        setProfileState((prev) => ({
          ...prev,
          dirty: true,
          error: undefined,
        }));
      }
    },
    [onUserChange]
  );

  const updateAddressesState = useCallback(
    (
      addresses: UserAddress[],
      resolveSelectedId?: (
        addresses: UserAddress[],
        prev: AddressUIState
      ) => number | undefined,
      options?: { closeModal?: boolean }
    ) => {
      setAddressState((prev) => {
        const preferredId = resolveSelectedId
          ? resolveSelectedId(addresses, prev)
          : undefined;

        const fallbackSelectedId =
          preferredId ??
          prev.selectedId ??
          addresses.find((address) => address.isDefault)?.id ??
          addresses[0]?.id;

        const selectedAddress =
          fallbackSelectedId != null
            ? addresses.find((address) => address.id === fallbackSelectedId)
            : undefined;

        addressesForcedSelection.current = false;

        return {
          items: addresses,
          loading: false,
          error: undefined,
          selectedId: selectedAddress?.id,
          modalOpen: options?.closeModal === false ? prev.modalOpen : false,
          editing: options?.closeModal === false ? prev.editing : null,
          confirmAction: null,
        } as AddressUIState;
      });
    },
    []
  );

  const delegate = useMemo<UserInfoFormControllerDelegate>(
    () => ({
      showLoading() {
        setAddressState((prev) => ({
          ...prev,
          loading: true,
          error: undefined,
        }));
      },
      hideLoading() {
        setAddressState((prev) => ({
          ...prev,
          loading: false,
        }));
      },
      didFetchAddresses(addresses) {
        updateAddressesState(addresses, undefined, { closeModal: false });
      },
      didCreateAddress(addresses) {
        updateAddressesState(
          addresses,
          (next, prev) =>
            next.find(
              (address) =>
                !prev.items.some((prevAddress) => prevAddress.id === address.id)
            )?.id
        );
      },
      didUpdateAddress(addresses) {
        updateAddressesState(addresses, (_, prev) => prev.selectedId);
      },
      didDeleteAddress(addresses) {
        updateAddressesState(addresses, (_, prev) => prev.selectedId);
      },
      didSetDefaultAddress(addresses) {
        updateAddressesState(
          addresses,
          (next) => next.find((address) => address.isDefault)?.id
        );
      },
      applicationShouldLogout() {
        onLogoutRef.current?.();
      },
      genericError(message) {
        setAddressState((prev) => ({
          ...prev,
          loading: false,
          error:
            message ?? "Ocorreu um erro inesperado ao gerenciar os endereços.",
        }));
      },
    }),
    [updateAddressesState]
  );

  const controllerRef = useRef<UserInfoFormController | null>(
    new UserInfoFormController(delegate)
  );

  useEffect(() => {
    controllerRef.current = new UserInfoFormController(delegate);
  }, [delegate]);

  const runWithAccessToken = useCallback(
    async (callback: (token: string) => Promise<void> | void) => {
      const token =
        getAccessToken?.() ??
        (typeof window !== "undefined"
          ? localStorage.getItem("AccessToken")
          : null);

      if (!token) {
        onLogoutRef.current?.();
        setAddressState((prev) => ({
          ...prev,
          loading: false,
        }));
        return;
      }

      await callback(token);
    },
    [getAccessToken]
  );

  const handleIdentificationChange = useCallback(
    (event: InputChangeEvent) => {
      const digitsOnly = sanitizeDigits(event.target.value).slice(0, 11);

      const nextErrors: UserErrors = {
        ...errors,
        identification: !validateIdentification(digitsOnly),
      };
      const nextUser: UserData = {
        ...user,
        identification: digitsOnly,
        identificationType: digitsOnly.length === 11 ? "CPF" : undefined,
      };

      emitUserChange(nextUser, nextErrors, { markDirty: true });
    },
    [emitUserChange, errors, user]
  );

  const handleCnpjChange = useCallback(
    (event: InputChangeEvent) => {
      const digitsOnly = sanitizeDigits(event.target.value).slice(0, 14);
      const nextErrors: UserErrors = {
        ...errors,
        cnpj: digitsOnly.length > 0 && !validateIdentification(digitsOnly),
      };
      const nextUser: UserData = {
        ...user,
        cnpj: digitsOnly || undefined,
      };
      emitUserChange(nextUser, nextErrors, { markDirty: true });
    },
    [emitUserChange, errors, user]
  );

  const handleBirthDateChange = useCallback(
    (event: InputChangeEvent) => {
      const digitsOnly = sanitizeDigits(event.target.value).slice(0, 8);
      const day = digitsOnly.slice(0, 2);
      const month = digitsOnly.slice(2, 4);
      const year = digitsOnly.slice(4, 8);

      const nextBirthDate = { day, month, year };
      const nextErrors: UserErrors = {
        ...errors,
        birthDate: !isValidBirthDate(nextBirthDate),
      };
      const nextUser: UserData = {
        ...user,
        birthDate: nextBirthDate,
      };

      emitUserChange(nextUser, nextErrors, { markDirty: true });
    },
    [emitUserChange, errors, user]
  );

  const handleCellphoneChange = useCallback(
    (event: InputChangeEvent) => {
      const digitsOnly = sanitizeDigits(event.target.value).slice(0, 11);
      const nextErrors: UserErrors = {
        ...errors,
        cellphone: digitsOnly.length < 10,
      };
      const nextUser: UserData = {
        ...user,
        cellphone: digitsOnly,
      };

      emitUserChange(nextUser, nextErrors, { markDirty: true });
    },
    [emitUserChange, errors, user]
  );

  const handleToggleSaveProfile = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setProfileState((prev) => ({
        ...prev,
        shouldSave: checked,
        dirty: checked ? prev.dirty : false,
        error: undefined,
      }));
    },
    []
  );

  const [hasCnpj, setHasCnpj] = useState(() => Boolean(user.cnpj));

  const applyLoadedProfile = useCallback(
    (profile: UserProfile) => {
      const nextUser = convertProfileToUserData(profile);
      emitUserChange(nextUser, INITIAL_ERRORS);
      setHasCnpj(Boolean(nextUser.cnpj));
      setProfileState({
        loading: false,
        saving: false,
        prefilled: true,
        dirty: false,
        shouldSave: true,
        error: undefined,
      });
    },
    [emitUserChange, setHasCnpj]
  );

  useEffect(() => {
    if (!loadUserProfile) {
      return;
    }

    let isCancelled = false;

    setProfileState((prev) => ({
      ...prev,
      loading: true,
      error: undefined,
    }));

    loadUserProfile()
      .then((profile) => {
        if (isCancelled) return;
        if (profile) {
          applyLoadedProfile(profile);
        } else {
          setProfileState({
            loading: false,
            saving: false,
            prefilled: false,
            dirty: false,
            shouldSave: false,
            error: undefined,
          });
        }
      })
      .catch((error: any) => {
        if (isCancelled) return;
        const message =
          error?.response?.data?.message ??
          error?.message ??
          "Não foi possível carregar seus dados salvos.";
        setProfileState({
          loading: false,
          saving: false,
          prefilled: false,
          dirty: false,
          shouldSave: false,
          error: message,
        });
      });

    return () => {
      isCancelled = true;
    };
  }, [loadUserProfile, applyLoadedProfile]);

  const ensureProfilePersisted = useCallback(async () => {
    if (!profileState.shouldSave) {
      return true;
    }

    if (!profileState.dirty && profileState.prefilled) {
      return true;
    }

    const payload = buildUserProfilePayload(user);
    if (!payload) {
      setProfileState((prev) => ({
        ...prev,
        error:
          "Preencha CPF/CNPJ, data de nascimento e celular válidos para salvar seus dados.",
      }));
      return false;
    }

    if (!saveUserProfile) {
      return true;
    }

    setProfileState((prev) => ({
      ...prev,
      saving: true,
      error: undefined,
    }));

    try {
      const saved = await saveUserProfile(payload);
      setProfileState((prev) => ({
        ...prev,
        saving: false,
        dirty: false,
        prefilled: true,
        error: undefined,
      }));

      if (saved?.identificationType) {
        const nextUser: UserData = {
          ...user,
          identificationType: saved.identificationType,
        };
        emitUserChange(nextUser, errors);
      }

      return true;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ??
        error?.message ??
        "Não foi possível salvar seus dados de perfil.";
      setProfileState((prev) => ({
        ...prev,
        saving: false,
        error: message,
      }));
      return false;
    }
  }, [profileState, saveUserProfile, user, emitUserChange, errors]);

  useEffect(() => {
    if (hasLoadedAddressesRef.current) {
      return;
    }
    hasLoadedAddressesRef.current = true;

    void runWithAccessToken(async (token) => {
      if (!token) {
        return;
      }
      await controllerRef.current?.listAddresses(token);
    });
  }, [runWithAccessToken]);

  const addresses = addressState.items;
  const selectedAddressId = addressState.selectedId;
  const selectedAddress = useMemo(
    () => addresses.find((address) => address.id === selectedAddressId),
    [addresses, selectedAddressId]
  );

  const selectedPrimaryHeading = useMemo(
    () => buildPrimaryHeading(selectedAddress),
    [selectedAddress]
  );

  const selectedSecondaryHeading = useMemo(
    () => buildSecondaryHeading(selectedAddress),
    [selectedAddress]
  );

  const birthDateValue = useMemo(
    () =>
      maskDate(
        `${user.birthDate.day}${user.birthDate.month}${user.birthDate.year}`
      ),
    [user.birthDate.day, user.birthDate.month, user.birthDate.year]
  );

  const handleToggleHasCnpj = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setHasCnpj(checked);
      if (!checked) {
        const nextUser: UserData = { ...user, cnpj: undefined };
        const nextErrors: UserErrors = { ...errors, cnpj: false };
        emitUserChange(nextUser, nextErrors, { markDirty: true });
      }
    },
    [user, errors, emitUserChange, setHasCnpj]
  );

  const [isSelectingAddress, setIsSelectingAddress] = useState(true);

  useEffect(() => {
    if (addresses.length === 0) {
      setIsSelectingAddress(true);
      addressesForcedSelection.current = false;
      return;
    }

    if (addressesForcedSelection.current) {
      return;
    }

    if (selectedAddress) {
      setIsSelectingAddress(false);
    } else {
      setIsSelectingAddress(true);
    }
  }, [addresses.length, selectedAddress]);

  const handleSelectAddress = useCallback((id: number) => {
    setAddressState((prev) => ({
      ...prev,
      selectedId: id,
      error: prev.error,
    }));
    addressesForcedSelection.current = false;
    setIsSelectingAddress(false);
  }, []);

  const handleSelectAnother = useCallback(() => {
    addressesForcedSelection.current = true;
    setIsSelectingAddress(true);
  }, []);

  const handleAddAddress = useCallback(() => {
    setAddressState((prev) => ({
      ...prev,
      modalOpen: true,
      editing: null,
      error: undefined,
    }));
  }, []);

  const handleEditAddress = useCallback((address: UserAddress) => {
    setAddressState((prev) => ({
      ...prev,
      modalOpen: true,
      editing: address,
      error: undefined,
    }));
  }, []);

  const handleCloseAddressModal = useCallback(() => {
    setAddressState((prev) => ({
      ...prev,
      modalOpen: false,
      editing: null,
    }));
  }, []);

  const handleSubmitAddressModal = useCallback(
    (data: UserAddressPayload, id?: number) => {
      setAddressState((prev) => ({
        ...prev,
        error: undefined,
      }));

      void runWithAccessToken(async (token) => {
        if (!token) {
          return;
        }

        if (id) {
          const patch: UserAddressPatchPayload = { ...data };
          await controllerRef.current?.updateAddress(token, id, patch);
        } else {
          await controllerRef.current?.createAddress(token, data);
        }
      });
    },
    [runWithAccessToken]
  );

  const handleDeleteAddress = useCallback((address: UserAddress) => {
    setAddressState((prev) => ({
      ...prev,
      confirmAction: { type: "delete", address },
    }));
  }, []);

  const handleSetDefaultAddress = useCallback((address: UserAddress) => {
    setAddressState((prev) => ({
      ...prev,
      confirmAction: { type: "default", address },
    }));
  }, []);

  const handleCancelAddressAction = useCallback(() => {
    setAddressState((prev) => ({
      ...prev,
      confirmAction: null,
    }));
  }, []);

  const handleConfirmAddressAction = useCallback(() => {
    const pending = addressState.confirmAction;
    if (!pending) {
      return;
    }

    void runWithAccessToken(async (token) => {
      if (!token) {
        return;
      }

      if (pending.type === "delete") {
        await controllerRef.current?.deleteAddress(token, pending.address.id);
      } else {
        await controllerRef.current?.setDefaultAddress(token, pending.address.id);
      }
    });

    setAddressState((prev) => ({
      ...prev,
      confirmAction: null,
    }));
  }, [addressState.confirmAction, runWithAccessToken]);

  useEffect(() => {
    const selected = addressState.items.find(
      (address) => address.id === addressState.selectedId
    );
    onAddressSelected?.(selected);
  }, [addressState.items, addressState.selectedId, onAddressSelected]);

  useEffect(() => {
    onAddressStateChange?.({
      loading: addressState.loading,
      selectedAddressId: addressState.selectedId,
      hasAddresses: addressState.items.length > 0,
      error: addressState.error,
    });
  }, [
    addressState.loading,
    addressState.selectedId,
    addressState.items.length,
    addressState.error,
    onAddressStateChange,
  ]);

  const profileBusy = profileState.loading || profileState.saving;

  useImperativeHandle(
    ref,
    () => ({
      ensureProfilePersisted,
      isBusy: () => profileBusy || addressState.loading,
    }),
    [ensureProfilePersisted, profileBusy, addressState.loading]
  );

  const showPrefillMessage =
    profileState.prefilled && !profileState.error && !profileState.loading;

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          Dados do Comprador
        </Typography>
      </Box>

      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="h6">Dados pessoais</Typography>
        {profileState.loading && (
          <Alert severity="info">Carregando dados salvos...</Alert>
        )}
        {profileState.saving && (
          <Alert severity="info">Salvando seus dados...</Alert>
        )}
        {showPrefillMessage && (
          <Alert severity="info">
            Usamos seus dados salvos do último pedido. Você pode alterá-los se quiser.
          </Alert>
        )}
        {profileState.error && (
          <Alert severity="error">{profileState.error}</Alert>
        )}
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          gap={2}
        >
          <TextField
            label="CPF"
            variant="outlined"
            fullWidth
            placeholder="000.000.000-00"
            required
            error={errors.identification}
            value={maskIdentification(user.identification)}
            onChange={handleIdentificationChange}
            sx={{ flex: 1 }}
            autoComplete="off"
            disabled={profileBusy}
          />

          <TextField
            variant="outlined"
            required
            label="Data de Nascimento"
            placeholder="DD/MM/AAAA"
            error={errors.birthDate}
            value={birthDateValue}
            onChange={handleBirthDateChange}
            sx={{ flex: 1 }}
            autoComplete="bday"
            disabled={profileBusy}
          />

          <TextField
            variant="outlined"
            required
            label="Celular"
            placeholder="(00) 00000-0000"
            value={maskPhone(user.cellphone)}
            error={errors.cellphone}
            onChange={handleCellphoneChange}
            sx={{ flex: 1 }}
            autoComplete="tel"
            disabled={profileBusy}
          />
        </Box>
        <Box>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={profileState.shouldSave}
                  onChange={handleToggleSaveProfile}
                  disabled={profileBusy}
                />
              }
              label="Salvar os dados acima para as próximas compras"
            />
          </Box>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasCnpj}
                  onChange={handleToggleHasCnpj}
                  disabled={profileBusy}
                />
              }
              label="Incluir CNPJ na compra"
            />
          </Box>
        </Box>
        {hasCnpj && (
          <TextField
            label="CNPJ"
            variant="outlined"
            placeholder="00.000.000/0000-00"
            error={errors.cnpj}
            value={maskCnpj(user.cnpj ?? "")}
            onChange={handleCnpjChange}
            autoComplete="off"
            disabled={profileBusy}
            sx={{ maxWidth: { md: "34%" } }}
          />
        )}
      </Box>

      <Divider />

      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="h6">Endereço de cobrança</Typography>

        {addressState.error && (
          <Alert severity="error">{addressState.error}</Alert>
        )}

        {isSelectingAddress || !selectedAddress ? (
          <AddressList
            addresses={addresses}
            onAdd={handleAddAddress}
            onEdit={handleEditAddress}
            onDelete={handleDeleteAddress}
            onSelect={handleSelectAddress}
            onSetDefault={handleSetDefaultAddress}
            selectedId={selectedAddressId}
            isLoading={addressState.loading}
          />
        ) : (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3,
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              backgroundColor: "grey.50",
              borderColor: "rgba(30,160,151,0.16)"
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1.5}
              flexWrap="wrap-reverse"
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Endereço de cobrança selecionado
                </Typography>
              </Box>
              <Button
                variant="text"
                size="medium"
                startIcon={<SwapHoriz fontSize="small" />}
                onClick={handleSelectAnother}
                sx={{
                  px: 2
                }}
              >
                Alterar
              </Button>
            </Box>

            <Typography variant="subtitle1" fontWeight={700}>
              {selectedPrimaryHeading}
            </Typography>
            {selectedSecondaryHeading && (
              <Typography variant="body2" color="text.secondary">
                {selectedSecondaryHeading}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {`${selectedAddress?.neighborhood ?? ""} - ${selectedAddress?.city ?? ""}/${selectedAddress?.state ?? ""}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              CEP: {maskCep(selectedAddress?.postalCode ?? "")}
            </Typography>
          </Paper>
        )}
      </Box>

      <AddressFormModal
        open={addressState.modalOpen}
        address={addressState.editing ?? undefined}
        onClose={handleCloseAddressModal}
        onSubmit={handleSubmitAddressModal}
        isSubmitting={addressState.loading}
      />

      {addressState.confirmAction && (
        <Dialog open onClose={handleCancelAddressAction} maxWidth="xs" fullWidth>
          <DialogTitle>Confirmar ação</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {addressState.confirmAction.type === "delete"
                ? `Deseja realmente excluir o endereço "${getUserAddressLabel(addressState.confirmAction.address)}"?`
                : `Deseja definir "${getUserAddressLabel(addressState.confirmAction.address)}" como endereço padrão?`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelAddressAction}>Cancelar</Button>
            <Button
              variant="contained"
              color={
                addressState.confirmAction.type === "delete"
                  ? "error"
                  : "primary"
              }
              onClick={handleConfirmAddressAction}
            >
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export const UserInfoForm = forwardRef(UserInfoFormComponent);

export default UserInfoForm;
